/** Builders (Jobs hub `H6IHDfRP` — Builders Build Brief 01 §4.5). A builder is a
 *  FIRM; a person at the firm who can sign in to the portal is a portal_user
 *  carrying `builder_id` (and no client). Builders sit outside Xero, Feefo and
 *  invoicing by construction, so there is no sync outcome to surface anywhere. */

import { api } from "./client";
import type {
  BuildersListResponse,
  BuilderDetailResponse,
  BuilderCreateInput,
  BuilderUpdateInput,
  BuilderRecord,
  BuilderInviteResponse,
  JobBuilderAttachInput,
  JobBuilderLinkUpdateInput,
  JobBuilderLinkResponse,
  JobBuilderDetachResponse,
} from "@/types";

/** GET /builders/list — every firm with live-job and people counts. No params;
 *  React does the search/filter over the in-memory set. */
export function listBuilders(): Promise<BuildersListResponse> {
  return api.get<BuildersListResponse>("jobs", "builders/list");
}

/** GET /builders/detail?id= — the firm, its portal logins and its jobs (live
 *  and past links). 404 if not found. */
export function getBuilderDetail(id: number): Promise<BuilderDetailResponse> {
  return api.get<BuilderDetailResponse>("jobs", "builders/detail", { query: { id } });
}

/** POST /builders/create — a firm record; company name is the only required
 *  field. Refetch the list on success. */
export function createBuilder(input: BuilderCreateInput): Promise<{ builder: BuilderRecord }> {
  return api.post<{ builder: BuilderRecord }>("jobs", "builders/create", { body: input });
}

/** PATCH /builders/{id} — presence-based partial patch: a field that is PRESENT
 *  in the body is written, including "" (which clears it); an omitted field is
 *  untouched. Company name may change but never blank. */
export function updateBuilder(id: number, input: BuilderUpdateInput): Promise<{ builder: BuilderRecord }> {
  return api.patch<{ builder: BuilderRecord }>("jobs", `builders/${id}`, { body: input });
}

/** POST /builders/archive — 400 while the firm is still attached to any job. */
export function archiveBuilder(id: number): Promise<{ builder: BuilderRecord }> {
  return api.post<{ builder: BuilderRecord }>("jobs", "builders/archive", { body: { id } });
}

/** POST /builders/reopen — back to active. */
export function reopenBuilder(id: number): Promise<{ builder: BuilderRecord }> {
  return api.post<{ builder: BuilderRecord }>("jobs", "builders/reopen", { body: { id } });
}

/** POST /builders/{id}/invite — create (or reactivate) the portal login for one
 *  person at the firm and, by default, email them the app download links (the
 *  same invite template clients get; no sign-in link inside). Idempotent for
 *  the same email, so it doubles as "resend". 400 if the email already belongs
 *  to a client or another firm — one row per email, so a person who is both
 *  needs two addresses. */
export function invitePerson(
  builderId: number,
  input: { name?: string; email: string; send_invite_email?: boolean },
): Promise<BuilderInviteResponse> {
  return api.post<BuilderInviteResponse>("jobs", `builders/${builderId}/invite`, { body: input });
}

/** POST /builders/{id}/people/{portal_user_id}/deactivate — switch one login
 *  off (a foreman leaves). Re-inviting the same email reactivates it. */
export function deactivatePerson(
  builderId: number,
  portalUserId: number,
): Promise<{ portal_user_id: number; is_active: boolean }> {
  return api.post("jobs", `builders/${builderId}/people/${portalUserId}/deactivate`, { body: {} });
}

/** POST /staff/jobs/{job_id}/builders — attach a firm to a job (or reactivate a
 *  past link). Their logins see the job's chat from now unless
 *  `show_full_history`. Posts a staff notice into the thread (D7). */
export function attachToJob(jobId: number, input: JobBuilderAttachInput): Promise<JobBuilderLinkResponse> {
  return api.post<JobBuilderLinkResponse>("jobs", `staff/jobs/${jobId}/builders`, { body: input });
}

/** PATCH /staff/jobs/{job_id}/builders/{builder_id} — role and/or the chat
 *  cutoff via bool flags (never an int 0 — the optional-int landmine). */
export function updateJobLink(
  jobId: number,
  builderId: number,
  input: JobBuilderLinkUpdateInput,
): Promise<JobBuilderLinkResponse> {
  return api.patch<JobBuilderLinkResponse>("jobs", `staff/jobs/${jobId}/builders/${builderId}`, { body: input });
}

/** DELETE /staff/jobs/{job_id}/builders/{builder_id} — detach; the firm's
 *  logins lose the chat immediately and the client sees a removal notice. */
export function detachFromJob(jobId: number, builderId: number): Promise<JobBuilderDetachResponse> {
  return api.delete<JobBuilderDetachResponse>("jobs", `staff/jobs/${jobId}/builders/${builderId}`);
}
